/**
 * 사용자 권한설정 — CM-DESIGN-001 확장 (업무권한 패키지 × 적용범위)
 *
 * 합의 구조:
 * - 업무그룹 폐기. 시스템 표준 업무권한 패키지를 사용자에게 배정하고 적용범위만 선택한다.
 * - 승인·확정·마감은 일반 패키지에 포함하지 않고 별도 승인책임으로 지정한다.
 * - 조합을 사전 생성하지 않는 희소 배정: 사용자당 실제 필요한 행만 저장한다.
 * - API(isBdApiEnabled): CmPermission · 아니면 localStorage
 */

import { isBdApiEnabled } from 'config/bdApiConfig';
import {
  cacheGet,
  cacheSet,
  commonSaveList,
  commonSelect,
  diffRowsByUid,
  parseJsonObject,
} from 'services/commonQuery';

const deptPermCache = new Map();
const deptScreenCache = new Map();
const adminPermCache = new Map();
const adminScreenCache = new Map();
const exceptionActionCache = new Map();

function packScopeDepts(row = {}) {
  return JSON.stringify({
    deptUids: row.deptUids || [],
    deptCodes: row.deptCodes || [],
    deptNames: row.deptNames || [],
  });
}

function unpackScopeDepts(json) {
  const o = parseJsonObject(json);
  return {
    deptUids: Array.isArray(o.deptUids) ? o.deptUids : [],
    deptCodes: Array.isArray(o.deptCodes) ? o.deptCodes : [],
    deptNames: Array.isArray(o.deptNames) ? o.deptNames : [],
  };
}

function mapDeptPermFromApi(row) {
  const scope = unpackScopeDepts(row.scopeDeptsJson);
  return {
    ...row,
    ...scope,
    mod: '',
  };
}

function mapDeptPermToApi(row) {
  return {
    ...row,
    scopeDeptsJson: packScopeDepts(row),
  };
}

function mapAdminPermFromApi(row) {
  return mapDeptPermFromApi(row);
}

function mapAdminPermToApi(row) {
  return mapDeptPermToApi(row);
}

const PERM_STORAGE_PREFIX = 'erp.master.userPerm';
const APPROVAL_STORAGE_PREFIX = 'erp.master.userApproval';

/** 일반 Action 라벨 (고위험 Action은 패키지에 포함하지 않음) */
export const PERM_ACTION_LABELS = {
  READ: '조회',
  CREATE: '추가',
  UPDATE: '수정',
  SAVE: '저장',
  EXPORT: '엑셀',
};

/**
 * 시스템 표준 업무권한 패키지
 * - 기관 담당자는 패키지를 새로 만들지 않고 배정만 한다 (기관 커스텀은 후속).
 * - 부서코드·승인 Action을 패키지에 넣지 않는다.
 */
export const STANDARD_PERM_PACKAGES = [
  {
    code: 'HR_VIEW',
    name: '인사 조회',
    module: '인사',
    actions: ['READ', 'EXPORT'],
    menus: ['대시보드', '발령 목록', '인사카드'],
  },
  {
    code: 'HR_STAFF',
    name: '인사 담당',
    module: '인사',
    actions: ['READ', 'CREATE', 'UPDATE', 'SAVE', 'EXPORT'],
    menus: ['인사마스터', '발령 목록', '인사카드', '부서관리'],
  },
  {
    code: 'BUD_VIEW',
    name: '예산 조회',
    module: '예산',
    actions: ['READ', 'EXPORT'],
    menus: ['예산 대시보드', '예산과목등록', '사업등록'],
  },
  {
    code: 'BUD_STAFF',
    name: '예산 편성담당',
    module: '예산',
    actions: ['READ', 'CREATE', 'UPDATE', 'SAVE', 'EXPORT'],
    menus: ['예산차수등록', '지출예산편성요구', '예산과목등록', '사업등록'],
  },
  {
    code: 'EXP_STAFF',
    name: '지출 담당',
    module: '지출',
    actions: ['READ', 'CREATE', 'UPDATE', 'SAVE', 'EXPORT'],
    menus: ['지출결의(예정)', '원인행위(예정)'],
  },
  {
    code: 'FIN_STAFF',
    name: '재무회계 담당',
    module: '회계',
    actions: ['READ', 'CREATE', 'UPDATE', 'SAVE', 'EXPORT'],
    menus: ['회계구분등록', '회계단위등록', '재원등록', '재원구성등록'],
  },
];

export const PERM_PACKAGE_OPTIONS = STANDARD_PERM_PACKAGES.map((p) => ({
  value: p.code,
  label: p.name,
}));

/** 적용범위 유형 */
export const SCOPE_TYPE_OPTIONS = [
  { value: 'OWN', label: '주관부서' },
  { value: 'OWN_SUB', label: '주관+하위부서' },
  { value: 'PICK', label: '선택부서' },
  { value: 'ALL', label: '기관전체' },
];

/** 승인책임 업무영역 */
export const APPROVAL_BIZ_OPTIONS = [
  { value: 'GEN', label: '일반업무' },
  { value: 'HR', label: '인사' },
  { value: 'BUD', label: '예산' },
  { value: 'EXP', label: '지출' },
  { value: 'FIN', label: '회계·결산' },
];

/** 승인책임 단계 (고위험 Action) */
export const APPROVAL_STAGE_OPTIONS = [
  { value: 'APPROVE', label: '승인' },
  { value: 'CONFIRM', label: '확정' },
  { value: 'CLOSE', label: '마감' },
];

export const YN_OPTIONS = [
  { value: 'Y', label: 'Y' },
  { value: 'N', label: 'N' },
];

export function permPackageByCode(code) {
  return STANDARD_PERM_PACKAGES.find((p) => p.code === code) || null;
}

export function permPackageLabel(code) {
  return permPackageByCode(code)?.name || code || '';
}

export function scopeTypeLabel(value) {
  return SCOPE_TYPE_OPTIONS.find((o) => o.value === value)?.label || value || '';
}

export function approvalBizLabel(value) {
  return APPROVAL_BIZ_OPTIONS.find((o) => o.value === value)?.label || value || '';
}

export function approvalStageLabel(value) {
  return APPROVAL_STAGE_OPTIONS.find((o) => o.value === value)?.label || value || '';
}

export function createPermUid(prefix = 'UP') {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function storageKey(prefix, tenantCode) {
  return `${prefix}|${String(tenantCode || '').trim()}`;
}

function readRows(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const data = JSON.parse(raw);
    return Array.isArray(data?.rows) ? data.rows : [];
  } catch {
    return [];
  }
}

function writeRows(key, rows) {
  const saved = (rows || [])
    .filter((r) => r && r.mod !== 'D')
    .map((r) => ({ ...r, mod: '' }));
  localStorage.setItem(
    key,
    JSON.stringify({ rows: saved, savedAt: new Date().toISOString() })
  );
  return saved;
}

export function loadUserPerms(tenantCode) {
  return readRows(storageKey(PERM_STORAGE_PREFIX, tenantCode)).map((r) => ({ ...r, mod: '' }));
}

export function saveUserPerms(tenantCode, rows) {
  return writeRows(
    storageKey(PERM_STORAGE_PREFIX, tenantCode),
    (rows || []).map((r) => ({ ...r, permUid: r.permUid || createPermUid('UP') }))
  );
}

export function loadUserApprovals(tenantCode) {
  return readRows(storageKey(APPROVAL_STORAGE_PREFIX, tenantCode)).map((r) => ({
    ...r,
    mod: '',
  }));
}

export function saveUserApprovals(tenantCode, rows) {
  return writeRows(
    storageKey(APPROVAL_STORAGE_PREFIX, tenantCode),
    (rows || []).map((r) => ({ ...r, apprUid: r.apprUid || createPermUid('UA') }))
  );
}

/** 선택부서 다건 → 표시 문자열 */
export function joinDeptNames(deptNames = []) {
  return (deptNames || []).filter(Boolean).join(', ');
}

function scopeNeedsDepts(scopeType) {
  return scopeType === 'PICK';
}

export function validateUserPerms(rows, users) {
  const active = (rows || []).filter((r) => r && r.mod !== 'D');
  const userByUid = new Map((users || []).map((u) => [u.accountUid, u]));
  const pairSet = new Set();

  for (const row of active) {
    const owner = userByUid.get(row.accountUid);
    if (!owner) return { ok: false, message: '업무권한에 연결된 사용자를 찾을 수 없습니다.' };
    const who = owner.loginId || '';
    if (!permPackageByCode(row.packageCode)) {
      return { ok: false, message: `${who}: 업무권한을 선택하세요.` };
    }
    if (!SCOPE_TYPE_OPTIONS.some((o) => o.value === row.scopeType)) {
      return { ok: false, message: `${who}: 적용범위를 선택하세요.` };
    }
    if (scopeNeedsDepts(row.scopeType) && !(row.deptUids || []).length) {
      return {
        ok: false,
        message: `${who} · ${permPackageLabel(row.packageCode)}: 선택부서 범위는 대상부서를 1개 이상 지정하세요.`,
      };
    }
    const pair = `${row.accountUid}|${row.packageCode}`;
    if (pairSet.has(pair)) {
      return {
        ok: false,
        message: `${who}: 같은 업무권한(${permPackageLabel(row.packageCode)})이 중복 배정되었습니다.`,
      };
    }
    pairSet.add(pair);
  }
  return { ok: true };
}

export function validateUserApprovals(rows, users) {
  const active = (rows || []).filter((r) => r && r.mod !== 'D');
  const userByUid = new Map((users || []).map((u) => [u.accountUid, u]));
  const pairSet = new Set();

  for (const row of active) {
    const owner = userByUid.get(row.accountUid);
    if (!owner) return { ok: false, message: '승인책임에 연결된 사용자를 찾을 수 없습니다.' };
    const who = owner.loginId || '';
    if (!APPROVAL_BIZ_OPTIONS.some((o) => o.value === row.bizArea)) {
      return { ok: false, message: `${who}: 책임업무를 선택하세요.` };
    }
    if (!APPROVAL_STAGE_OPTIONS.some((o) => o.value === row.stage)) {
      return { ok: false, message: `${who}: 책임단계(승인/확정/마감)를 선택하세요.` };
    }
    if (!SCOPE_TYPE_OPTIONS.some((o) => o.value === row.scopeType)) {
      return { ok: false, message: `${who}: 승인책임 적용범위를 선택하세요.` };
    }
    if (scopeNeedsDepts(row.scopeType) && !(row.deptUids || []).length) {
      return {
        ok: false,
        message: `${who} · ${approvalBizLabel(row.bizArea)} ${approvalStageLabel(row.stage)}: 대상부서를 지정하세요.`,
      };
    }
    const pair = `${row.accountUid}|${row.bizArea}|${row.stage}`;
    if (pairSet.has(pair)) {
      return {
        ok: false,
        message: `${who}: 같은 승인책임(${approvalBizLabel(row.bizArea)} · ${approvalStageLabel(row.stage)})이 중복되었습니다.`,
      };
    }
    pairSet.add(pair);
  }
  return { ok: true };
}

function scopeSentence(row, userRow) {
  if (row.scopeType === 'ALL') return '기관 전체';
  if (row.scopeType === 'OWN') return `주관부서(${userRow?.deptName || '미지정'})`;
  if (row.scopeType === 'OWN_SUB') return `주관부서(${userRow?.deptName || '미지정'})와 하위부서`;
  const names = joinDeptNames(row.deptNames);
  return names ? `선택부서(${names})` : '선택부서(미지정)';
}

/**
 * 최종 권한 미리보기 문장 생성
 * @returns {string[]}
 */
export function buildPermPreview(userRow, permRows = [], approvalRows = []) {
  if (!userRow) return [];
  const lines = [];
  const activePerms = permRows.filter((r) => r.mod !== 'D' && r.useYn !== 'N');
  const activeApprovals = approvalRows.filter((r) => r.mod !== 'D' && r.useYn !== 'N');

  activePerms.forEach((row) => {
    const pkg = permPackageByCode(row.packageCode);
    if (!pkg) return;
    const actions = pkg.actions.map((a) => PERM_ACTION_LABELS[a] || a).join('·');
    lines.push(
      `[${pkg.name}] ${scopeSentence(row, userRow)} 데이터를 ${actions}할 수 있습니다. (화면: ${pkg.menus.join(', ')})`
    );
  });
  if (!activePerms.length) {
    lines.push('배정된 업무권한이 없습니다. 접근 가능한 업무 화면이 없습니다.');
  }

  activeApprovals.forEach((row) => {
    lines.push(
      `[승인책임] ${approvalBizLabel(row.bizArea)} ${approvalStageLabel(row.stage)} — ${scopeSentence(row, userRow)} 대상.`
    );
  });
  if (!activeApprovals.length) {
    lines.push('승인·확정·마감 권한은 없습니다.');
  }
  return lines;
}

/* -------------------------------------------------------------------------- */
/* 부서 기본권한 + 책임자 권한 + 사용자 예외 (2차 PoC)                         */
/* -------------------------------------------------------------------------- */

const DEPT_PERMISSION_STORAGE_PREFIX = 'erp.master.deptPermission';
const DEPT_SCREEN_STORAGE_PREFIX = 'erp.master.deptScreenPermission';
const ADMIN_PERMISSION_STORAGE_PREFIX = 'erp.master.adminPermission';
const ADMIN_SCREEN_STORAGE_PREFIX = 'erp.master.adminScreenPermission';
const EXCEPTION_ACTION_STORAGE_PREFIX = 'erp.master.exceptionActionPermission';
const RESPONSIBILITY_STORAGE_PREFIX = 'erp.master.responsibilityPermission';
const EXCEPTION_STORAGE_PREFIX = 'erp.master.permissionException';

/** 시스템이 분류하는 업무 모듈. 기관 담당자는 모듈을 생성하지 않고 권한만 배정한다. */
export const AUTH_MODULE_OPTIONS = [
  { value: 'BASE', label: '기준정보' },
  { value: 'HR', label: '인사' },
  { value: 'BUD', label: '예산' },
  { value: 'EXP', label: '지출' },
  { value: 'INC', label: '수입' },
  { value: 'ACC', label: '회계' },
];

/**
 * 부서 기본권한에 현재 반영하는 모듈 (PoC 구현분)
 * — 모듈 추가 시 여기만 늘리면 매트릭스 행이 자동으로 늘어난다.
 */
export const DEPT_AUTH_MODULE_OPTIONS = [
  { value: 'BASE', label: '기준정보' },
  { value: 'HR', label: '인사' },
  { value: 'BUD', label: '예산' },
];

/**
 * 부서 메뉴구성 — 조회·등록·전체 (의사결정·승인은 사용자 권한설정의 의사결정 탭)
 * 전체(FULL) = 조회+등록 포함
 */
export const DEPT_MENU_LEVEL_OPTIONS = [
  { value: 'READ', label: '조회' },
  { value: 'REGISTER', label: '등록' },
  { value: 'FULL', label: '전체' },
];

/** 사용자 권한 · 의사결정(승인) 탭에 노출하는 승인성 메뉴 */
export const DECISION_MENU_OPTIONS = [
  { value: 'APPROVE', label: '승인' },
  { value: 'CONFIRM', label: '확정' },
  { value: 'CLOSE', label: '마감' },
  { value: 'REJECT', label: '반려' },
];

/** 사용자 권한에 현재 반영하는 모듈 — 부서권한과 동일 PoC 범위 */
export const USER_AUTH_MODULE_OPTIONS = DEPT_AUTH_MODULE_OPTIONS;

/** 부서 기준 데이터범위 */
export const DEPT_SCOPE_OPTIONS = [
  { value: 'OWN', label: '해당부서' },
  { value: 'OWN_SUB', label: '해당+하위부서' },
  { value: 'PICK', label: '선택부서' },
  { value: 'ALL', label: '기관전체' },
];

/** 사용자 기준 데이터범위 */
export const USER_SCOPE_OPTIONS = [
  { value: 'OWN', label: '주관부서' },
  { value: 'OWN_SUB', label: '주관+하위부서' },
  { value: 'PICK', label: '선택부서' },
  { value: 'ALL', label: '기관전체' },
];

/** @deprecated 레거시 책임자 레벨 — 로드 시 의사결정 메뉴 매트릭스로 변환 */
export const RESPONSIBILITY_LEVEL_OPTIONS = [
  { value: 'DECISION', label: '의사결정' },
  { value: 'FULL', label: '전체' },
];

export const EXCEPTION_TYPE_OPTIONS = [
  { value: 'GRANT', label: '추가' },
  { value: 'DENY', label: '제한' },
];

/** 예외에서 다루는 메뉴구성 — 조회·등록·전체 (+ 제한 시 의사결정) */
export const EXCEPTION_PERMISSION_OPTIONS = [
  { value: 'READ', label: '조회' },
  { value: 'REGISTER', label: '등록' },
  { value: 'FULL', label: '전체' },
  { value: 'DECISION', label: '의사결정' },
];

export function authModuleLabel(code) {
  return (
    DEPT_AUTH_MODULE_OPTIONS.find((o) => o.value === code)?.label ||
    AUTH_MODULE_OPTIONS.find((o) => o.value === code)?.label ||
    code ||
    ''
  );
}

export function deptMenuLevelLabel(code) {
  return DEPT_MENU_LEVEL_OPTIONS.find((o) => o.value === code)?.label || code || '';
}

export function decisionMenuLabel(code) {
  return DECISION_MENU_OPTIONS.find((o) => o.value === code)?.label || code || '';
}

export function responsibilityLevelLabel(code) {
  return (
    decisionMenuLabel(code) ||
    RESPONSIBILITY_LEVEL_OPTIONS.find((o) => o.value === code)?.label ||
    code ||
    ''
  );
}

export function exceptionTypeLabel(code) {
  return EXCEPTION_TYPE_OPTIONS.find((o) => o.value === code)?.label || code || '';
}

export function exceptionPermissionLabel(code) {
  return EXCEPTION_PERMISSION_OPTIONS.find((o) => o.value === code)?.label || code || '';
}

export async function loadDeptPermissions(tenantCode) {
  const tc = String(tenantCode || '').trim();
  if (isBdApiEnabled()) {
    const rows = await commonSelect('CmPermission.selectDeptPermissionList', { tenantCode: tc });
    const mapped = normalizeDeptPermissionRows((rows || []).map(mapDeptPermFromApi)).map((r) => ({
      ...r,
      mod: '',
    }));
    cacheSet(deptPermCache, tc, mapped);
    return mapped;
  }
  const mapped = normalizeDeptPermissionRows(
    readRows(storageKey(DEPT_PERMISSION_STORAGE_PREFIX, tenantCode))
  ).map((r) => ({
    ...r,
    mod: '',
  }));
  cacheSet(deptPermCache, tc, mapped);
  return mapped;
}

export async function saveDeptPermissions(tenantCode, rows) {
  const tc = String(tenantCode || '').trim();
  // 부여(Y)된 매트릭스 행만 저장 — 화면은 항상 모듈×메뉴구성 전체를 다시 펼친다
  const granted = (rows || [])
    .filter((r) => r && r.mod !== 'D' && r.grantedYn === 'Y')
    .map((r) => ({
      ...r,
      deptPermUid: r.deptPermUid || createPermUid('DP'),
      menuLevel: r.menuLevel,
      grantedYn: 'Y',
    }));

  if (isBdApiEnabled()) {
    const prev = cacheGet(deptPermCache, tc) || (await loadDeptPermissions(tc));
    const dirty = diffRowsByUid(prev, granted, 'deptPermUid', () => createPermUid('DP')).map((r) =>
      r.mod === 'D' ? r : mapDeptPermToApi(r)
    );
    await commonSaveList(
      [
        'CmPermission.insertDeptPermission',
        'CmPermission.updateDeptPermission',
        'CmPermission.deleteDeptPermission',
      ],
      dirty,
      { tenantCode: tc }
    );
    return loadDeptPermissions(tc);
  }

  const saved = writeRows(storageKey(DEPT_PERMISSION_STORAGE_PREFIX, tenantCode), granted);
  cacheSet(deptPermCache, tc, saved);
  return saved;
}

/* -------------------------------------------------------------------------- */
/* 부서 화면구성 (프로그램 접근 화이트리스트)                                   */
/* -------------------------------------------------------------------------- */

export async function loadDeptScreenPermissions(tenantCode) {
  const tc = String(tenantCode || '').trim();
  if (isBdApiEnabled()) {
    const rows = await commonSelect('CmPermission.selectDeptScreenPermissionList', {
      tenantCode: tc,
    });
    const mapped = (rows || []).map((r) => ({ ...r, mod: '' }));
    cacheSet(deptScreenCache, tc, mapped);
    return mapped;
  }
  const mapped = readRows(storageKey(DEPT_SCREEN_STORAGE_PREFIX, tenantCode)).map((r) => ({
    ...r,
    mod: '',
  }));
  cacheSet(deptScreenCache, tc, mapped);
  return mapped;
}

export async function saveDeptScreenPermissions(tenantCode, rows) {
  const tc = String(tenantCode || '').trim();
  const granted = (rows || [])
    .filter((r) => r && r.mod !== 'D' && r.grantedYn === 'Y')
    .map((r) => ({
      ...r,
      deptScreenUid: r.deptScreenUid || createPermUid('DS'),
      grantedYn: 'Y',
    }));

  if (isBdApiEnabled()) {
    const prev = cacheGet(deptScreenCache, tc) || (await loadDeptScreenPermissions(tc));
    const dirty = diffRowsByUid(prev, granted, 'deptScreenUid', () => createPermUid('DS'));
    await commonSaveList(
      [
        'CmPermission.insertDeptScreenPermission',
        'CmPermission.updateDeptScreenPermission',
        'CmPermission.deleteDeptScreenPermission',
      ],
      dirty,
      { tenantCode: tc }
    );
    return loadDeptScreenPermissions(tc);
  }

  const saved = writeRows(storageKey(DEPT_SCREEN_STORAGE_PREFIX, tenantCode), granted);
  cacheSet(deptScreenCache, tc, saved);
  return saved;
}

/**
 * 부서 선택 시 화면구성 트리 — 메뉴 계층 + leaf 부여 반영
 * @param {string} deptUid
 * @param {Array} savedRows
 * @param {Array} programTree buildDeptAuthProgramTree() 결과
 */
export function buildDeptScreenTree(deptUid, savedRows = [], programTree = []) {
  if (!deptUid) return [];
  const saved = (savedRows || []).filter((r) => r.deptUid === deptUid && r.mod !== 'D');
  const byScreen = new Map(saved.map((r) => [r.screenId, r]));

  const cloneApply = (nodes) =>
    (nodes || []).map((n) => {
      const id = `${deptUid}|${n.id}`;
      if (n.subRows?.length) {
        const subRows = cloneApply(n.subRows);
        const allOn = subRows.length > 0 && subRows.every((c) => c.grantedYn === 'Y');
        return {
          ...n,
          id,
          treeKey: n.id,
          deptUid,
          subRows,
          grantedYn: allOn ? 'Y' : 'N',
          screenId: '',
          deptScreenUid: '',
          mod: '',
        };
      }
      const hit = byScreen.get(n.screenId);
      return {
        ...n,
        id,
        treeKey: n.id,
        deptUid,
        deptScreenUid: hit?.deptScreenUid || createPermUid('DS'),
        grantedYn: hit?.grantedYn === 'Y' ? 'Y' : 'N',
        remark: hit?.remark || '',
        mod: '',
      };
    });

  return cloneApply(programTree);
}

/** @deprecated flat 매트릭스 — 트리 빌더 사용 권장 */
export function buildDeptScreenMatrix(deptUid, savedRows = [], programCatalog = []) {
  if (!deptUid) return [];
  const saved = (savedRows || []).filter((r) => r.deptUid === deptUid && r.mod !== 'D');
  const byScreen = new Map(saved.map((r) => [r.screenId, r]));
  return (programCatalog || []).map((prog) => {
    const hit = byScreen.get(prog.screenId);
    return {
      deptScreenUid: hit?.deptScreenUid || createPermUid('DS'),
      id: `${deptUid}|${prog.screenId}`,
      deptUid,
      screenId: prog.screenId,
      progId: prog.progId,
      screenName: prog.screenName,
      menuName: prog.screenName,
      moduleCode: prog.moduleCode,
      moduleName: prog.moduleName,
      menuType: prog.menuType,
      menuTypeNm: prog.menuTypeNm,
      menuPath: prog.menuPath,
      menuNo: prog.menuNo,
      grantedYn: hit?.grantedYn === 'Y' ? 'Y' : 'N',
      remark: hit?.remark || '',
      mod: '',
    };
  });
}

/** 트리 노드 체크 시 하위 전체 동일 값 + 상위 동기화 */
export function cascadeDeptScreenGrant(nodes = [], targetId, grantedYn) {
  const val = grantedYn === 'Y' ? 'Y' : 'N';
  const setDesc = (arr) =>
    (arr || []).map((n) => ({
      ...n,
      grantedYn: val,
      subRows: n.subRows?.length ? setDesc(n.subRows) : n.subRows,
    }));

  const apply = (arr) =>
    (arr || []).map((n) => {
      if (String(n.id) === String(targetId)) {
        return {
          ...n,
          grantedYn: val,
          subRows: n.subRows?.length ? setDesc(n.subRows) : n.subRows,
        };
      }
      if (n.subRows?.length) {
        return { ...n, subRows: apply(n.subRows) };
      }
      return n;
    });

  return syncDeptScreenParentGrants(apply(nodes));
}

/** 자식 모두 Y이면 부모 Y, 아니면 N */
export function syncDeptScreenParentGrants(nodes = []) {
  return (nodes || []).map((n) => {
    if (!n.subRows?.length) return { ...n };
    const subRows = syncDeptScreenParentGrants(n.subRows);
    const allOn = subRows.every((c) => c.grantedYn === 'Y');
    return { ...n, subRows, grantedYn: allOn ? 'Y' : 'N' };
  });
}

/** 트리 전체 ON/OFF */
export function bulkSetDeptScreenTree(nodes = [], grantedYn) {
  const val = grantedYn === 'Y' ? 'Y' : 'N';
  const walk = (arr) =>
    (arr || []).map((n) => ({
      ...n,
      grantedYn: val,
      subRows: n.subRows?.length ? walk(n.subRows) : n.subRows,
    }));
  return walk(nodes);
}

/** 부여된 leaf 화면만 추출 (저장용) */
export function collectGrantedDeptScreens(nodes = [], deptUid) {
  const out = [];
  const walk = (arr) => {
    (arr || []).forEach((n) => {
      if (n.subRows?.length) {
        walk(n.subRows);
        return;
      }
      if (n.screenId && n.grantedYn === 'Y') {
        out.push({
          deptScreenUid: n.deptScreenUid || createPermUid('DS'),
          deptUid: deptUid || n.deptUid,
          screenId: n.screenId,
          progId: n.progId || n.screenId,
          screenName: n.menuName || n.screenName || n.screenId,
          moduleCode: n.moduleCode,
          moduleName: n.moduleName,
          menuType: n.menuType,
          menuTypeNm: n.menuTypeNm,
          menuPath: n.menuPath,
          menuNo: n.menuNo,
          grantedYn: 'Y',
          remark: n.remark || '',
          mod: '',
        });
      }
    });
  };
  walk(nodes);
  return out;
}

export function countGrantedScreensInTree(nodes = []) {
  let n = 0;
  const walk = (arr) => {
    (arr || []).forEach((row) => {
      if (row.subRows?.length) walk(row.subRows);
      else if (row.screenId && row.grantedYn === 'Y') n += 1;
    });
  };
  walk(nodes);
  return n;
}

/** @deprecated 모듈별 일괄 — 트리 cascade 사용 권장 */
export function bulkSetDeptScreens(rows = [], grantedYn, { moduleCode } = {}) {
  return (rows || []).map((r) => {
    if (moduleCode && r.moduleCode !== moduleCode) return { ...r };
    return { ...r, grantedYn: grantedYn === 'Y' ? 'Y' : 'N' };
  });
}

export function validateDeptScreenPermissions(rows, depts, programCatalog = []) {
  const active = (rows || []).filter((r) => r && r.mod !== 'D' && r.grantedYn === 'Y');
  const deptByUid = new Map((depts || []).map((r) => [r.deptUid, r]));
  const screenSet = new Set((programCatalog || []).map((p) => p.screenId));
  const pairs = new Set();

  for (const row of active) {
    const dept = deptByUid.get(row.deptUid);
    if (!dept) return { ok: false, message: '화면구성에 연결된 부서를 찾을 수 없습니다.' };
    if (screenSet.size && !screenSet.has(row.screenId)) {
      return {
        ok: false,
        message: `${dept.deptName || dept.deptCode}: 알 수 없는 화면(${row.screenId})입니다.`,
      };
    }
    const pair = `${row.deptUid}|${row.screenId}`;
    if (pairs.has(pair)) {
      return {
        ok: false,
        message: `${dept.deptName || dept.deptCode}: 화면 ${row.screenName || row.screenId}이(가) 중복되었습니다.`,
      };
    }
    pairs.add(pair);
  }
  return { ok: true };
}

/* -------------------------------------------------------------------------- */
/* 관리자 권한설정 (사용자 × 화면구성·권한구성)                                */
/* -------------------------------------------------------------------------- */

export async function loadAdminScreenPermissions(tenantCode) {
  const tc = String(tenantCode || '').trim();
  if (isBdApiEnabled()) {
    const rows = await commonSelect('CmPermission.selectAdminScreenPermissionList', {
      tenantCode: tc,
    });
    const mapped = (rows || []).map((r) => ({ ...r, mod: '' }));
    cacheSet(adminScreenCache, tc, mapped);
    return mapped;
  }
  const mapped = readRows(storageKey(ADMIN_SCREEN_STORAGE_PREFIX, tenantCode)).map((r) => ({
    ...r,
    mod: '',
  }));
  cacheSet(adminScreenCache, tc, mapped);
  return mapped;
}

export async function saveAdminScreenPermissions(tenantCode, rows) {
  const tc = String(tenantCode || '').trim();
  const granted = (rows || [])
    .filter((r) => r && r.mod !== 'D' && r.grantedYn === 'Y')
    .map((r) => ({
      ...r,
      adminScreenUid: r.adminScreenUid || createPermUid('AS'),
      grantedYn: 'Y',
    }));

  if (isBdApiEnabled()) {
    const prev = cacheGet(adminScreenCache, tc) || (await loadAdminScreenPermissions(tc));
    const dirty = diffRowsByUid(prev, granted, 'adminScreenUid', () => createPermUid('AS'));
    await commonSaveList(
      [
        'CmPermission.insertAdminScreenPermission',
        'CmPermission.updateAdminScreenPermission',
        'CmPermission.deleteAdminScreenPermission',
      ],
      dirty,
      { tenantCode: tc }
    );
    return loadAdminScreenPermissions(tc);
  }

  const saved = writeRows(storageKey(ADMIN_SCREEN_STORAGE_PREFIX, tenantCode), granted);
  cacheSet(adminScreenCache, tc, saved);
  return saved;
}

export async function loadAdminPermissions(tenantCode) {
  const tc = String(tenantCode || '').trim();
  if (isBdApiEnabled()) {
    const rows = await commonSelect('CmPermission.selectAdminPermissionList', { tenantCode: tc });
    const mapped = normalizeDeptPermissionRows((rows || []).map(mapAdminPermFromApi)).map((r) => ({
      ...r,
      mod: '',
    }));
    cacheSet(adminPermCache, tc, mapped);
    return mapped;
  }
  const mapped = normalizeDeptPermissionRows(
    readRows(storageKey(ADMIN_PERMISSION_STORAGE_PREFIX, tenantCode))
  ).map((r) => ({ ...r, mod: '' }));
  cacheSet(adminPermCache, tc, mapped);
  return mapped;
}

export async function saveAdminPermissions(tenantCode, rows) {
  const tc = String(tenantCode || '').trim();
  const granted = (rows || [])
    .filter((r) => r && r.mod !== 'D' && r.grantedYn === 'Y')
    .map((r) => ({
      ...r,
      adminPermUid: r.adminPermUid || r.deptPermUid || createPermUid('AP'),
      menuLevel: r.menuLevel,
      grantedYn: 'Y',
    }));

  if (isBdApiEnabled()) {
    const prev = cacheGet(adminPermCache, tc) || (await loadAdminPermissions(tc));
    const dirty = diffRowsByUid(prev, granted, 'adminPermUid', () => createPermUid('AP')).map((r) =>
      r.mod === 'D' ? r : mapAdminPermToApi(r)
    );
    await commonSaveList(
      [
        'CmPermission.insertAdminPermission',
        'CmPermission.updateAdminPermission',
        'CmPermission.deleteAdminPermission',
      ],
      dirty,
      { tenantCode: tc }
    );
    return loadAdminPermissions(tc);
  }

  const saved = writeRows(storageKey(ADMIN_PERMISSION_STORAGE_PREFIX, tenantCode), granted);
  cacheSet(adminPermCache, tc, saved);
  return saved;
}

/** 관리자 화면구성 트리 (부서 화면구성과 동일 구조, 소유자=accountUid) */
export function buildAdminScreenTree(accountUid, savedRows = [], programTree = []) {
  if (!accountUid) return [];
  const saved = (savedRows || []).filter((r) => r.accountUid === accountUid && r.mod !== 'D');
  const byScreen = new Map(saved.map((r) => [r.screenId, r]));

  const cloneApply = (nodes) =>
    (nodes || []).map((n) => {
      const id = `${accountUid}|${n.id}`;
      if (n.subRows?.length) {
        const subRows = cloneApply(n.subRows);
        const allOn = subRows.length > 0 && subRows.every((c) => c.grantedYn === 'Y');
        return {
          ...n,
          id,
          treeKey: n.id,
          accountUid,
          subRows,
          grantedYn: allOn ? 'Y' : 'N',
          screenId: '',
          adminScreenUid: '',
          mod: '',
        };
      }
      const hit = byScreen.get(n.screenId);
      return {
        ...n,
        id,
        treeKey: n.id,
        accountUid,
        adminScreenUid: hit?.adminScreenUid || createPermUid('AS'),
        grantedYn: hit?.grantedYn === 'Y' ? 'Y' : 'N',
        remark: hit?.remark || '',
        mod: '',
      };
    });

  return cloneApply(programTree);
}

export function collectGrantedAdminScreens(nodes = [], accountUid) {
  const out = [];
  const walk = (arr) => {
    (arr || []).forEach((n) => {
      if (n.subRows?.length) {
        walk(n.subRows);
        return;
      }
      if (n.screenId && n.grantedYn === 'Y') {
        out.push({
          adminScreenUid: n.adminScreenUid || createPermUid('AS'),
          accountUid: accountUid || n.accountUid,
          screenId: n.screenId,
          progId: n.progId || n.screenId,
          screenName: n.menuName || n.screenName || n.screenId,
          moduleCode: n.moduleCode,
          moduleName: n.moduleName,
          menuType: n.menuType,
          menuTypeNm: n.menuTypeNm,
          menuPath: n.menuPath,
          menuNo: n.menuNo,
          grantedYn: 'Y',
          remark: n.remark || '',
          mod: '',
        });
      }
    });
  };
  walk(nodes);
  return out;
}

export function buildAdminPermissionMatrix(accountUid, savedRows = []) {
  if (!accountUid) return [];
  const saved = normalizeDeptPermissionRows(
    (savedRows || []).filter((r) => r.accountUid === accountUid && r.mod !== 'D')
  );
  const byKey = new Map(saved.map((r) => [`${r.moduleCode}|${r.menuLevel}`, r]));
  const scopeByModule = new Map();
  saved.forEach((r) => {
    if (!scopeByModule.has(r.moduleCode)) scopeByModule.set(r.moduleCode, r);
  });

  const rows = [];
  USER_AUTH_MODULE_OPTIONS.forEach((mod) => {
    const scopeSrc = scopeByModule.get(mod.value) || {};
    DEPT_MENU_LEVEL_OPTIONS.forEach((level) => {
      const key = `${mod.value}|${level.value}`;
      const hit = byKey.get(key);
      rows.push({
        adminPermUid: hit?.adminPermUid || hit?.deptPermUid || createPermUid('AP'),
        id: `${accountUid}|${mod.value}|${level.value}`,
        accountUid,
        moduleCode: mod.value,
        moduleName: mod.label,
        menuLevel: level.value,
        menuLevelNm: level.label,
        grantedYn: hit?.grantedYn === 'Y' ? 'Y' : 'N',
        scopeType: scopeSrc.scopeType || hit?.scopeType || 'OWN',
        deptUids: [...(scopeSrc.deptUids || hit?.deptUids || [])],
        deptCodes: [...(scopeSrc.deptCodes || hit?.deptCodes || [])],
        deptNames: [...(scopeSrc.deptNames || hit?.deptNames || [])],
        useYn: 'Y',
        remark: hit?.remark || '',
        grantLocked: false,
        mod: '',
      });
    });
  });
  return applyDeptFullImplication(rows);
}

export function validateAdminPermissions(rows, users) {
  const active = (rows || []).filter((r) => r && r.mod !== 'D' && r.grantedYn === 'Y');
  const userByUid = new Map((users || []).map((r) => [r.accountUid, r]));
  const pairs = new Set();

  for (const row of active) {
    const owner = userByUid.get(row.accountUid);
    if (!owner) return { ok: false, message: '관리자 권한에 연결된 사용자를 찾을 수 없습니다.' };
    const ownerLabel = `${owner.loginId || owner.userNm} · ${authModuleLabel(row.moduleCode)} · ${deptMenuLevelLabel(row.menuLevel)}`;
    if (!USER_AUTH_MODULE_OPTIONS.some((o) => o.value === row.moduleCode)) {
      return { ok: false, message: `${owner.loginId || owner.userNm}: 지원하지 않는 모듈입니다.` };
    }
    if (!DEPT_MENU_LEVEL_OPTIONS.some((o) => o.value === row.menuLevel)) {
      return { ok: false, message: `${ownerLabel}: 메뉴구성이 올바르지 않습니다.` };
    }
    const scopeValidation = validateScope(row, ownerLabel);
    if (!scopeValidation.ok) return scopeValidation;
    const pair = `${row.accountUid}|${row.moduleCode}|${row.menuLevel}`;
    if (pairs.has(pair)) {
      return { ok: false, message: `${ownerLabel}: 같은 권한이 중복되었습니다.` };
    }
    pairs.add(pair);
  }
  return { ok: true };
}

export function validateAdminScreenPermissions(rows, users, programCatalog = []) {
  const active = (rows || []).filter((r) => r && r.mod !== 'D' && r.grantedYn === 'Y');
  const userByUid = new Map((users || []).map((r) => [r.accountUid, r]));
  const screenSet = new Set((programCatalog || []).map((p) => p.screenId));
  const pairs = new Set();

  for (const row of active) {
    const owner = userByUid.get(row.accountUid);
    if (!owner) return { ok: false, message: '화면구성에 연결된 사용자를 찾을 수 없습니다.' };
    if (screenSet.size && !screenSet.has(row.screenId)) {
      return {
        ok: false,
        message: `${owner.loginId || owner.userNm}: 알 수 없는 화면(${row.screenId})입니다.`,
      };
    }
    const pair = `${row.accountUid}|${row.screenId}`;
    if (pairs.has(pair)) {
      return {
        ok: false,
        message: `${owner.loginId || owner.userNm}: 화면 ${row.screenName || row.screenId}이(가) 중복되었습니다.`,
      };
    }
    pairs.add(pair);
  }
  return { ok: true };
}

/* -------------------------------------------------------------------------- */
/* 예외권한설정 — 프로그램 × 버튼(Action) 컨트롤                               */
/* -------------------------------------------------------------------------- */

/** 화면별 버튼(액션) 컨트롤 대상 */
export const EXCEPTION_BUTTON_OPTIONS = [
  { value: 'SEARCH', label: '조회' },
  { value: 'ADD', label: '추가' },
  { value: 'DELETE', label: '삭제' },
  { value: 'SAVE', label: '저장' },
  { value: 'EXCEL', label: '엑셀' },
  { value: 'APPROVE', label: '승인' },
  { value: 'CONFIRM', label: '확정' },
  { value: 'CLOSE', label: '마감' },
  { value: 'PRINT', label: '인쇄' },
];

export function exceptionButtonLabel(code) {
  return EXCEPTION_BUTTON_OPTIONS.find((o) => o.value === code)?.label || code || '';
}

export async function loadExceptionActions(tenantCode) {
  const tc = String(tenantCode || '').trim();
  if (isBdApiEnabled()) {
    const rows = await commonSelect('CmPermission.selectExceptionActionList', { tenantCode: tc });
    const mapped = (rows || []).map((r) => ({ ...r, mod: '' }));
    cacheSet(exceptionActionCache, tc, mapped);
    return mapped;
  }
  const mapped = readRows(storageKey(EXCEPTION_ACTION_STORAGE_PREFIX, tenantCode)).map((r) => ({
    ...r,
    mod: '',
  }));
  cacheSet(exceptionActionCache, tc, mapped);
  return mapped;
}

export async function saveExceptionActions(tenantCode, rows) {
  const tc = String(tenantCode || '').trim();
  const active = (rows || [])
    .filter((r) => r && r.mod !== 'D' && r.appliedYn === 'Y')
    .map((r) => ({
      ...r,
      exceptionActionUid: r.exceptionActionUid || createPermUid('EA'),
      appliedYn: 'Y',
    }));

  if (isBdApiEnabled()) {
    const prev = cacheGet(exceptionActionCache, tc) || (await loadExceptionActions(tc));
    const dirty = diffRowsByUid(prev, active, 'exceptionActionUid', () => createPermUid('EA'));
    await commonSaveList(
      [
        'CmPermission.insertExceptionAction',
        'CmPermission.updateExceptionAction',
        'CmPermission.deleteExceptionAction',
      ],
      dirty,
      { tenantCode: tc }
    );
    return loadExceptionActions(tc);
  }

  const saved = writeRows(storageKey(EXCEPTION_ACTION_STORAGE_PREFIX, tenantCode), active);
  cacheSet(exceptionActionCache, tc, saved);
  return saved;
}

/** 선택 프로그램의 버튼 컨트롤 행 */
export function buildExceptionButtonMatrix(accountUid, screenId, savedRows = []) {
  if (!accountUid || !screenId) return [];
  const saved = (savedRows || []).filter(
    (r) => r.accountUid === accountUid && r.screenId === screenId && r.mod !== 'D'
  );
  const byAction = new Map(saved.map((r) => [r.actionCode, r]));
  return EXCEPTION_BUTTON_OPTIONS.map((btn) => {
    const hit = byAction.get(btn.value);
    return {
      exceptionActionUid: hit?.exceptionActionUid || createPermUid('EA'),
      id: `${accountUid}|${screenId}|${btn.value}`,
      accountUid,
      screenId,
      actionCode: btn.value,
      actionName: btn.label,
      exceptionType: hit?.exceptionType || 'DENY',
      appliedYn: hit?.appliedYn === 'Y' ? 'Y' : 'N',
      reason: hit?.reason || '',
      mod: '',
    };
  });
}

export function validateExceptionActions(rows, users, programCatalog = []) {
  const active = (rows || []).filter((r) => r && r.mod !== 'D' && r.appliedYn === 'Y');
  const userByUid = new Map((users || []).map((r) => [r.accountUid, r]));
  const screenSet = new Set((programCatalog || []).map((p) => p.screenId));
  const pairs = new Set();

  for (const row of active) {
    const owner = userByUid.get(row.accountUid);
    if (!owner) return { ok: false, message: '예외권한에 연결된 사용자를 찾을 수 없습니다.' };
    const ownerLabel = `${owner.loginId || owner.userNm}`;
    if (screenSet.size && !screenSet.has(row.screenId)) {
      return { ok: false, message: `${ownerLabel}: 알 수 없는 화면(${row.screenId})입니다.` };
    }
    if (!EXCEPTION_BUTTON_OPTIONS.some((o) => o.value === row.actionCode)) {
      return { ok: false, message: `${ownerLabel}: 버튼(액션)이 올바르지 않습니다.` };
    }
    if (!EXCEPTION_TYPE_OPTIONS.some((o) => o.value === row.exceptionType)) {
      return { ok: false, message: `${ownerLabel}: 예외 유형을 선택하세요.` };
    }
    const pair = `${row.accountUid}|${row.screenId}|${row.actionCode}|${row.exceptionType}`;
    if (pairs.has(pair)) {
      return { ok: false, message: `${ownerLabel}: 같은 버튼 예외가 중복되었습니다.` };
    }
    pairs.add(pair);
  }
  return { ok: true };
}

/**
 * 구버전(모듈당 1행·readYn/outputYn/registerYn) → 매트릭스(모듈×메뉴구성)로 변환
 */
export function normalizeDeptPermissionRows(rows = []) {
  const out = [];
  for (const row of rows || []) {
    if (!row) continue;
    if (row.menuLevel) {
      out.push({
        ...row,
        grantedYn: row.grantedYn === 'Y' || row.grantedYn === true ? 'Y' : row.grantedYn === 'N' ? 'N' : 'Y',
        menuLevelNm: row.menuLevelNm || deptMenuLevelLabel(row.menuLevel),
        moduleName: row.moduleName || authModuleLabel(row.moduleCode),
      });
      continue;
    }
    // 레거시: 조회·출력 → READ, 등록 → REGISTER, 둘 다 → FULL 로 축약
    const hasRead = row.readYn === 'Y' || row.outputYn === 'Y';
    const hasReg = row.registerYn === 'Y';
    const base = {
      deptPermUid: row.deptPermUid || createPermUid('DP'),
      deptUid: row.deptUid,
      moduleCode: row.moduleCode,
      moduleName: authModuleLabel(row.moduleCode),
      scopeType: row.scopeType || 'OWN',
      deptUids: row.deptUids || [],
      deptCodes: row.deptCodes || [],
      deptNames: row.deptNames || [],
      useYn: row.useYn || 'Y',
      remark: row.remark || '',
      grantedYn: 'Y',
    };
    if (hasRead && hasReg) {
      out.push({
        ...base,
        deptPermUid: createPermUid('DP'),
        menuLevel: 'FULL',
        menuLevelNm: '전체',
      });
    } else {
      if (hasRead) {
        out.push({
          ...base,
          deptPermUid: row.deptPermUid || createPermUid('DP'),
          menuLevel: 'READ',
          menuLevelNm: '조회',
        });
      }
      if (hasReg) {
        out.push({
          ...base,
          deptPermUid: createPermUid('DP'),
          menuLevel: 'REGISTER',
          menuLevelNm: '등록',
        });
      }
    }
  }
  return out;
}

/** 전체(FULL)=Y 이면 같은 모듈의 조회·등록도 Y (표시·저장 일관성) */
export function applyDeptFullImplication(rows = []) {
  const list = (rows || []).map((r) => ({ ...r }));
  const fullModules = new Set(
    list.filter((r) => r.menuLevel === 'FULL' && r.grantedYn === 'Y' && r.mod !== 'D').map((r) => r.moduleCode)
  );
  return list.map((r) => {
    if (r.mod === 'D') return r;
    if (!fullModules.has(r.moduleCode)) {
      return { ...r, grantLocked: false };
    }
    if (r.menuLevel === 'READ' || r.menuLevel === 'REGISTER') {
      return { ...r, grantedYn: 'Y', grantLocked: true };
    }
    return { ...r, grantLocked: false };
  });
}

/**
 * 부서 선택 시 우측 그리드용 고정 매트릭스 (모듈 × 조회/등록/전체)
 */
export function buildDeptPermissionMatrix(deptUid, savedRows = []) {
  if (!deptUid) return [];
  const saved = normalizeDeptPermissionRows(
    (savedRows || []).filter((r) => r.deptUid === deptUid && r.mod !== 'D')
  );
  const byKey = new Map(saved.map((r) => [`${r.moduleCode}|${r.menuLevel}`, r]));
  const scopeByModule = new Map();
  saved.forEach((r) => {
    if (!scopeByModule.has(r.moduleCode)) scopeByModule.set(r.moduleCode, r);
  });

  const rows = [];
  DEPT_AUTH_MODULE_OPTIONS.forEach((mod) => {
    const scopeSrc = scopeByModule.get(mod.value) || {};
    DEPT_MENU_LEVEL_OPTIONS.forEach((level) => {
      const key = `${mod.value}|${level.value}`;
      const hit = byKey.get(key);
      rows.push({
        deptPermUid: hit?.deptPermUid || createPermUid('DP'),
        id: `${deptUid}|${mod.value}|${level.value}`,
        deptUid,
        moduleCode: mod.value,
        moduleName: mod.label,
        menuLevel: level.value,
        menuLevelNm: level.label,
        grantedYn: hit?.grantedYn === 'Y' ? 'Y' : 'N',
        scopeType: scopeSrc.scopeType || hit?.scopeType || 'OWN',
        deptUids: [...(scopeSrc.deptUids || hit?.deptUids || [])],
        deptCodes: [...(scopeSrc.deptCodes || hit?.deptCodes || [])],
        deptNames: [...(scopeSrc.deptNames || hit?.deptNames || [])],
        useYn: 'Y',
        remark: hit?.remark || '',
        grantLocked: false,
        mod: '',
      });
    });
  });
  return applyDeptFullImplication(rows);
}

/** 메뉴구성 일괄 부여/해제 — FULL ON 시 조회·등록도 함께 Y, FULL OFF 시 전부 N */
export function bulkSetDeptMenuLevel(rows = [], menuLevel, grantedYn) {
  // 권한 전체 토글 OFF → 조회·등록·전체 모두 해제
  if (menuLevel === 'FULL' && grantedYn !== 'Y') {
    return (rows || []).map((r) => {
      if (r.mod === 'D') return { ...r };
      return { ...r, grantedYn: 'N', grantLocked: false };
    });
  }

  const next = (rows || []).map((r) => {
    if (r.mod === 'D' || r.menuLevel !== menuLevel) return { ...r };
    return { ...r, grantedYn: grantedYn === 'Y' ? 'Y' : 'N' };
  });
  if (menuLevel !== 'FULL' && grantedYn !== 'Y') {
    // 조회/등록 OFF 시 해당 모듈 FULL도 OFF
    return applyDeptFullImplication(
      next.map((r) => {
        if (r.menuLevel !== 'FULL') return r;
        const siblingOff = next.find(
          (x) =>
            x.moduleCode === r.moduleCode &&
            x.menuLevel === menuLevel &&
            x.grantedYn !== 'Y'
        );
        return siblingOff ? { ...r, grantedYn: 'N' } : r;
      })
    );
  }
  return applyDeptFullImplication(next);
}

/**
 * 부여 체크 변경 후 매트릭스 동기화
 * - FULL=Y → 조회·등록 Y + 잠금
 * - 조회/등록을 N으로 바꾸면 같은 모듈 FULL도 N
 */
export function syncDeptGrantChange(rows = [], changedRow) {
  if (!changedRow) return applyDeptFullImplication(rows);
  let next = (rows || []).map((r) => {
    if (r.deptPermUid === changedRow.deptPermUid || r.id === changedRow.id) {
      return { ...r, ...changedRow };
    }
    return { ...r };
  });

  if (changedRow.menuLevel === 'FULL') {
    return applyDeptFullImplication(next);
  }

  if (changedRow.grantedYn !== 'Y') {
    next = next.map((r) => {
      if (r.moduleCode === changedRow.moduleCode && r.menuLevel === 'FULL') {
        return { ...r, grantedYn: 'N' };
      }
      return r;
    });
  }
  return applyDeptFullImplication(next);
}

/** 같은 모듈의 데이터범위·선택부서를 모든 메뉴구성 행에 맞춤 */
export function syncDeptModuleScope(rows = [], moduleCode, scopePatch = {}) {
  return (rows || []).map((r) => {
    if (r.moduleCode !== moduleCode) return r;
    return {
      ...r,
      ...scopePatch,
    };
  });
}

/**
 * 레거시 책임자 행(authorityLevel DECISION|FULL) → 모듈×승인메뉴 매트릭스 행으로 펼침
 */
export function normalizeResponsibilityRows(rows = []) {
  const out = [];
  for (const row of rows || []) {
    if (!row) continue;
    if (row.decisionMenu) {
      out.push({
        ...row,
        grantedYn: row.grantedYn === 'Y' || row.grantedYn === true ? 'Y' : row.grantedYn === 'N' ? 'N' : 'Y',
        decisionMenuNm: row.decisionMenuNm || decisionMenuLabel(row.decisionMenu),
        moduleName: row.moduleName || authModuleLabel(row.moduleCode),
      });
      continue;
    }
    // 구버전: 모듈당 DECISION/FULL 1행 → 승인·확정·마감·반려 전부 부여로 변환
    if (row.authorityLevel === 'DECISION' || row.authorityLevel === 'FULL') {
      DECISION_MENU_OPTIONS.forEach((menu, idx) => {
        out.push({
          responsibilityUid:
            idx === 0 && row.responsibilityUid ? row.responsibilityUid : createPermUid('RP'),
          accountUid: row.accountUid,
          moduleCode: row.moduleCode,
          moduleName: authModuleLabel(row.moduleCode),
          decisionMenu: menu.value,
          decisionMenuNm: menu.label,
          grantedYn: 'Y',
          scopeType: row.scopeType || 'OWN',
          deptUids: [...(row.deptUids || [])],
          deptCodes: [...(row.deptCodes || [])],
          deptNames: [...(row.deptNames || [])],
          startDate: row.startDate || '',
          endDate: row.endDate || '',
          useYn: row.useYn || 'Y',
          reason: row.reason || '',
        });
      });
    }
  }
  return out;
}

/** 사용자 선택 시 우측 의사결정 탭용 고정 매트릭스 (모듈 × 승인/확정/마감/반려) */
export function buildDecisionPermissionMatrix(accountUid, savedRows = []) {
  if (!accountUid) return [];
  const saved = normalizeResponsibilityRows(
    (savedRows || []).filter((r) => r.accountUid === accountUid && r.mod !== 'D')
  );
  const byKey = new Map(saved.map((r) => [`${r.moduleCode}|${r.decisionMenu}`, r]));
  const scopeByModule = new Map();
  saved.forEach((r) => {
    if (!scopeByModule.has(r.moduleCode)) scopeByModule.set(r.moduleCode, r);
  });

  const rows = [];
  USER_AUTH_MODULE_OPTIONS.forEach((mod) => {
    const scopeSrc = scopeByModule.get(mod.value) || {};
    DECISION_MENU_OPTIONS.forEach((menu) => {
      const key = `${mod.value}|${menu.value}`;
      const hit = byKey.get(key);
      rows.push({
        responsibilityUid: hit?.responsibilityUid || createPermUid('RP'),
        id: `${accountUid}|${mod.value}|${menu.value}`,
        accountUid,
        moduleCode: mod.value,
        moduleName: mod.label,
        decisionMenu: menu.value,
        decisionMenuNm: menu.label,
        grantedYn: hit?.grantedYn === 'Y' ? 'Y' : 'N',
        scopeType: scopeSrc.scopeType || hit?.scopeType || 'OWN',
        deptUids: [...(scopeSrc.deptUids || hit?.deptUids || [])],
        deptCodes: [...(scopeSrc.deptCodes || hit?.deptCodes || [])],
        deptNames: [...(scopeSrc.deptNames || hit?.deptNames || [])],
        startDate: hit?.startDate || scopeSrc.startDate || '',
        endDate: hit?.endDate || scopeSrc.endDate || '',
        useYn: 'Y',
        reason: hit?.reason || scopeSrc.reason || '',
        mod: '',
      });
    });
  });
  return rows;
}

/** 의사결정 메뉴 일괄 부여/해제 */
export function bulkSetDecisionMenus(rows = [], decisionMenu, grantedYn) {
  return (rows || []).map((r) => {
    if (r.mod === 'D' || r.decisionMenu !== decisionMenu) return { ...r };
    return { ...r, grantedYn: grantedYn === 'Y' ? 'Y' : 'N' };
  });
}

export function syncDecisionGrantChange(rows = [], changedRow) {
  if (!changedRow) return (rows || []).map((r) => ({ ...r }));
  return (rows || []).map((r) => {
    if (r.responsibilityUid === changedRow.responsibilityUid || r.id === changedRow.id) {
      return { ...r, ...changedRow };
    }
    return { ...r };
  });
}

/** 같은 모듈의 범위·기간·사유를 모든 승인메뉴 행에 맞춤 */
export function syncDecisionModuleScope(rows = [], moduleCode, scopePatch = {}) {
  return (rows || []).map((r) => {
    if (r.moduleCode !== moduleCode) return r;
    return { ...r, ...scopePatch };
  });
}

export function loadResponsibilityPermissions(tenantCode) {
  return normalizeResponsibilityRows(
    readRows(storageKey(RESPONSIBILITY_STORAGE_PREFIX, tenantCode))
  ).map((r) => ({
    ...r,
    mod: '',
  }));
}

export function saveResponsibilityPermissions(tenantCode, rows) {
  // 부여(Y)된 의사결정 매트릭스 행만 저장
  const granted = (rows || []).filter((r) => r && r.mod !== 'D' && r.grantedYn === 'Y');
  return writeRows(
    storageKey(RESPONSIBILITY_STORAGE_PREFIX, tenantCode),
    granted.map((r) => ({
      ...r,
      responsibilityUid: r.responsibilityUid || createPermUid('RP'),
      decisionMenu: r.decisionMenu,
      grantedYn: 'Y',
      authorityLevel: undefined,
    }))
  );
}

/** 레거시 OUTPUT → READ */
export function normalizeExceptionRows(rows = []) {
  return (rows || []).map((r) => {
    if (!r) return r;
    const permissionType = r.permissionType === 'OUTPUT' ? 'READ' : r.permissionType;
    return {
      ...r,
      permissionType,
      permissionTypeNm: exceptionPermissionLabel(permissionType),
      moduleName: r.moduleName || authModuleLabel(r.moduleCode),
    };
  });
}

export function loadPermissionExceptions(tenantCode) {
  return normalizeExceptionRows(
    readRows(storageKey(EXCEPTION_STORAGE_PREFIX, tenantCode))
  ).map((r) => ({
    ...r,
    mod: '',
  }));
}

export function savePermissionExceptions(tenantCode, rows) {
  return writeRows(
    storageKey(EXCEPTION_STORAGE_PREFIX, tenantCode),
    normalizeExceptionRows(rows || []).map((r) => ({
      ...r,
      exceptionUid: r.exceptionUid || createPermUid('PE'),
    }))
  );
}

/**
 * 유효권한 미리보기 문구 (PoC)
 * 우선순위: DENY 예외 > GRANT 예외 > 의사결정 부여 > 부서 기본권한
 */
export function buildEffectivePermissionPreview({
  accountUid,
  deptUid,
  deptPermissions = [],
  decisionRows = [],
  exceptionRows = [],
  asOfDate,
} = {}) {
  if (!accountUid) return '사용자를 선택하면 유효권한 요약을 표시합니다.';
  const today = asOfDate || new Date().toISOString().slice(0, 10);
  const inPeriod = (r) =>
    (!r.startDate || String(r.startDate) <= today) &&
    (!r.endDate || String(r.endDate) >= today) &&
    r.useYn !== 'N' &&
    r.mod !== 'D';

  const lines = [];
  USER_AUTH_MODULE_OPTIONS.forEach((mod) => {
    const deptLevels = (deptPermissions || [])
      .filter(
        (r) =>
          r.deptUid === deptUid &&
          r.moduleCode === mod.value &&
          r.grantedYn === 'Y' &&
          r.mod !== 'D'
      )
      .map((r) => deptMenuLevelLabel(r.menuLevel));
    const decisions = (decisionRows || [])
      .filter(
        (r) =>
          r.accountUid === accountUid &&
          r.moduleCode === mod.value &&
          r.grantedYn === 'Y' &&
          inPeriod(r)
      )
      .map((r) => decisionMenuLabel(r.decisionMenu));
    const grants = (exceptionRows || [])
      .filter(
        (r) =>
          r.accountUid === accountUid &&
          r.moduleCode === mod.value &&
          r.exceptionType === 'GRANT' &&
          inPeriod(r)
      )
      .map((r) => exceptionPermissionLabel(r.permissionType));
    const denies = (exceptionRows || [])
      .filter(
        (r) =>
          r.accountUid === accountUid &&
          r.moduleCode === mod.value &&
          r.exceptionType === 'DENY' &&
          inPeriod(r)
      )
      .map((r) => exceptionPermissionLabel(r.permissionType));

    const parts = [];
    if (deptLevels.length) parts.push(`부서 ${[...new Set(deptLevels)].join('·')}`);
    if (decisions.length) parts.push(`의사결정 ${[...new Set(decisions)].join('·')}`);
    if (grants.length) parts.push(`예외추가 ${[...new Set(grants)].join('·')}`);
    if (denies.length) parts.push(`예외제한 ${[...new Set(denies)].join('·')}`);
    if (parts.length) {
      lines.push(`${mod.label}: ${parts.join(' / ')}`);
    }
  });

  if (!lines.length) {
    return '부여된 부서·의사결정·예외 권한이 없습니다. (제한 예외가 있으면 해당 권한이 우선 차단됩니다.)';
  }
  return `유효권한 요약 (제한 > 추가 > 의사결정 > 부서)\n${lines.join('\n')}`;
}

function isValidScope(scopeType) {
  return DEPT_SCOPE_OPTIONS.some((o) => o.value === scopeType);
}

function validateScope(row, ownerLabel) {
  if (!isValidScope(row.scopeType)) {
    return { ok: false, message: `${ownerLabel}: 데이터범위를 선택하세요.` };
  }
  if (row.scopeType === 'PICK' && !(row.deptUids || []).length) {
    return { ok: false, message: `${ownerLabel}: 선택부서를 1개 이상 지정하세요.` };
  }
  return { ok: true };
}

function validateDateRange(row, ownerLabel) {
  if (!row.startDate || !row.endDate) {
    return { ok: false, message: `${ownerLabel}: 적용 시작일과 종료일은 필수입니다.` };
  }
  if (String(row.startDate) > String(row.endDate)) {
    return { ok: false, message: `${ownerLabel}: 종료일은 시작일보다 빠를 수 없습니다.` };
  }
  return { ok: true };
}

export function validateDeptPermissions(rows, depts) {
  const active = (rows || []).filter((r) => r && r.mod !== 'D' && r.grantedYn === 'Y');
  const deptByUid = new Map((depts || []).map((r) => [r.deptUid, r]));
  const pairs = new Set();

  for (const row of active) {
    const dept = deptByUid.get(row.deptUid);
    if (!dept) return { ok: false, message: '권한에 연결된 부서를 찾을 수 없습니다.' };
    const ownerLabel = `${dept.deptName || dept.deptCode} · ${authModuleLabel(row.moduleCode)} · ${deptMenuLevelLabel(row.menuLevel)}`;
    if (!DEPT_AUTH_MODULE_OPTIONS.some((o) => o.value === row.moduleCode)) {
      return { ok: false, message: `${dept.deptName || dept.deptCode}: 지원하지 않는 모듈입니다.` };
    }
    if (!DEPT_MENU_LEVEL_OPTIONS.some((o) => o.value === row.menuLevel)) {
      return { ok: false, message: `${ownerLabel}: 메뉴구성(조회·등록·전체)이 올바르지 않습니다.` };
    }
    const scopeValidation = validateScope(row, ownerLabel);
    if (!scopeValidation.ok) return scopeValidation;
    const pair = `${row.deptUid}|${row.moduleCode}|${row.menuLevel}`;
    if (pairs.has(pair)) {
      return { ok: false, message: `${ownerLabel}: 같은 권한이 중복되었습니다.` };
    }
    pairs.add(pair);
  }
  return { ok: true };
}

export function validateResponsibilityPermissions(rows, users) {
  const active = (rows || []).filter((r) => r && r.mod !== 'D' && r.grantedYn === 'Y');
  const userByUid = new Map((users || []).map((r) => [r.accountUid, r]));
  const pairs = new Set();

  for (const row of active) {
    const owner = userByUid.get(row.accountUid);
    if (!owner) return { ok: false, message: '의사결정 권한에 연결된 사용자를 찾을 수 없습니다.' };
    const ownerLabel = `${owner.loginId || owner.userNm} · ${authModuleLabel(row.moduleCode)} · ${decisionMenuLabel(row.decisionMenu)}`;
    if (!USER_AUTH_MODULE_OPTIONS.some((o) => o.value === row.moduleCode)) {
      return { ok: false, message: `${owner.loginId || owner.userNm}: 지원하지 않는 모듈입니다.` };
    }
    if (!DECISION_MENU_OPTIONS.some((o) => o.value === row.decisionMenu)) {
      return { ok: false, message: `${ownerLabel}: 승인 메뉴가 올바르지 않습니다.` };
    }
    const scopeValidation = validateScope(row, ownerLabel);
    if (!scopeValidation.ok) return scopeValidation;
    const dateValidation = validateDateRange(row, ownerLabel);
    if (!dateValidation.ok) return dateValidation;
    if (!String(row.reason || '').trim()) {
      return { ok: false, message: `${ownerLabel}: 의사결정 지정 사유를 입력하세요.` };
    }
    const pair = `${row.accountUid}|${row.moduleCode}|${row.decisionMenu}`;
    if (pairs.has(pair)) {
      return { ok: false, message: `${ownerLabel}: 같은 의사결정 권한이 중복되었습니다.` };
    }
    pairs.add(pair);
  }
  return { ok: true };
}

export function validatePermissionExceptions(rows, users) {
  const active = normalizeExceptionRows(rows || []).filter((r) => r && r.mod !== 'D');
  const userByUid = new Map((users || []).map((r) => [r.accountUid, r]));
  const pairs = new Set();

  for (const row of active) {
    const owner = userByUid.get(row.accountUid);
    if (!owner) return { ok: false, message: '권한 예외에 연결된 사용자를 찾을 수 없습니다.' };
    const ownerLabel = `${owner.loginId || owner.userNm} · ${authModuleLabel(row.moduleCode)}`;
    if (!USER_AUTH_MODULE_OPTIONS.some((o) => o.value === row.moduleCode)) {
      return { ok: false, message: `${owner.loginId || owner.userNm}: 지원하지 않는 모듈입니다.` };
    }
    if (!EXCEPTION_TYPE_OPTIONS.some((o) => o.value === row.exceptionType)) {
      return { ok: false, message: `${ownerLabel}: 예외 유형을 선택하세요.` };
    }
    if (!EXCEPTION_PERMISSION_OPTIONS.some((o) => o.value === row.permissionType)) {
      return { ok: false, message: `${ownerLabel}: 권한구성을 선택하세요.` };
    }
    // 의사결정 추가는 의사결정 탭에서만 — 예외 GRANT로 DECISION 불가
    if (row.exceptionType === 'GRANT' && row.permissionType === 'DECISION') {
      return {
        ok: false,
        message: `${ownerLabel}: 의사결정 권한 추가는 「의사결정(승인)」 탭에서 처리하세요.`,
      };
    }
    const scopeValidation = validateScope(row, ownerLabel);
    if (!scopeValidation.ok) return scopeValidation;
    const dateValidation = validateDateRange(row, ownerLabel);
    if (!dateValidation.ok) return dateValidation;
    if (!String(row.reason || '').trim()) {
      return { ok: false, message: `${ownerLabel}: 예외 적용 사유를 입력하세요.` };
    }
    const pair = [
      row.accountUid,
      row.moduleCode,
      row.exceptionType,
      row.permissionType,
      row.startDate,
      row.endDate,
    ].join('|');
    if (pairs.has(pair)) {
      return { ok: false, message: `${ownerLabel}: 같은 기간의 권한 예외가 중복되었습니다.` };
    }
    pairs.add(pair);
  }
  return { ok: true };
}
